import React, { useContext, useEffect, useState, useRef } from "react";
import { useRouter } from "next/router";
import { AudioPlayerContext } from "../context/AudioPlayerContext";
import { FaPlay, FaPause } from "react-icons/fa";
import Link from "next/link";
import { useTheaterMode } from '../context/TheaterModeContext';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css'; // Import Tippy.js CSS

const GlobalAudioPlayer = ({ theNextChapter }) => {
  const { isPlaying, audioSource, playAudio, pauseAudio, audioRef } = useContext(AudioPlayerContext);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(null);
  const router = useRouter();
  const siteVersion = router.pathname.substring(0, 3);
  const { asPath } = router;
  const { theaterMode } = useTheaterMode();

  const aiRoutes = [
    '/en/ai',
    '/fr/ia',
  ];

  const isAI = aiRoutes.some(route => asPath.startsWith(route));

  const [lastAudio, setLastAudio] = useState(() => {
    if (typeof window !== "undefined") {
      const storedAudio = localStorage.getItem("lastAudio");
      return storedAudio || (siteVersion === "/fr" ? "https://looybi-images.fra1.cdn.digitaloceanspaces.com/listen/sunulife-bienvenue.mp3" : "https://looybi-images.fra1.cdn.digitaloceanspaces.com/listen/sunulife-welcome.mp3");
    }
    return (siteVersion === "/fr" ? "https://looybi-images.fra1.cdn.digitaloceanspaces.com/listen/sunulife-bienvenue.mp3" : "https://looybi-images.fra1.cdn.digitaloceanspaces.com/listen/sunulife-welcome.mp3");
  });

  const [seeking, setSeeking] = useState(false);
  const [seekValue, setSeekValue] = useState(currentTime);
  const [isLoading, setIsLoading] = useState(false);
  const rafRef = useRef(null);

  const formatTime = (time) => {
    if (!isFinite(time) || time === Infinity || time === 0 || time > 10800) return "∞";
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);
    return hours > 0
      ? `${hours}:${minutes < 10 ? "0" : ""}${minutes}:${seconds < 10 ? "0" : ""}${seconds}`
      : `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const saveLastAudio = (source) => {
    if (typeof window !== "undefined") {
      localStorage.setItem("lastAudio", source);
      setLastAudio(source);
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateDuration = () => {
      if (isFinite(audio.duration)) {
        setDuration(Math.floor(audio.duration));
      }
    };

    const updateTime = () => {
      if (!seeking) {
        setCurrentTime(Math.floor(audio.currentTime));
      }
    };

    const handleEnded = () => {
      pauseAudio();
      setCurrentTime(0);

      // Navigate to the next chapter
      if (theNextChapter) {
        router.push(theNextChapter);
      }
    };

    const handleWaiting = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);
    const handleCanPlayThrough = () => setIsLoading(false);
    const handlePlaying = () => setIsLoading(false);

    if (audio.readyState >= 1) updateDuration();

    audio.addEventListener("loadedmetadata", updateDuration);
    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("waiting", handleWaiting);
    audio.addEventListener("canplay", handleCanPlay);
    audio.addEventListener("canplaythrough", handleCanPlayThrough);
    audio.addEventListener("playing", handlePlaying);

    return () => {
      audio.removeEventListener("loadedmetadata", updateDuration);
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("waiting", handleWaiting);
      audio.removeEventListener("canplay", handleCanPlay);
      audio.removeEventListener("canplaythrough", handleCanPlayThrough);
      audio.removeEventListener("playing", handlePlaying);
    };
  }, [audioRef, pauseAudio, seeking, theNextChapter, router]);

  useEffect(() => {
    if (isPlaying && audioSource) {
      saveLastAudio(audioSource);
    }
  }, [isPlaying, audioSource]);

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      pauseAudio();
    } else {
      const sourceToPlay = audioSource || lastAudio;
      playAudio(sourceToPlay);
      saveLastAudio(sourceToPlay);
    }
  };

  const handleSeek = (value) => {
    const audio = audioRef.current;
    if (audio && duration !== null) {
      audio.currentTime = value;
      setCurrentTime(value);
    }
  };

  const handleSeekChange = (e) => {
    const value = Number(e.target.value);
    setSeekValue(value);

    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    rafRef.current = requestAnimationFrame(() => {
      handleSeek(value);
      rafRef.current = null;
    });
  };

  useEffect(() => {
    if (!seeking) {
      setSeekValue(currentTime);
    }
  }, [currentTime, seeking]);

  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  useEffect(() => {
    const handleRouteChange = (url) => {
      const pathsToStop = ["/en/music/", "/fr/music/","/en/world-music/", "/fr/world-music/", "/en/videos/", "/fr/videos/", "/en/moments/", "/fr/moments/", "/en/tv/", "/fr/tv/"];
      if (pathsToStop.some((path) => url.startsWith(path))) {
        const audio = audioRef.current;
        if (audio) {
          audio.pause();
          pauseAudio();
        }
      }
    };

    router.events.on("routeChangeStart", handleRouteChange);

    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [audioRef, pauseAudio, router.events]);

  return (
    <>
      <Link href="#" onClick={(e) => { e.preventDefault(); handlePlayPause(); }}>
        <div className="nav-bot-menu">
          <div>{isPlaying ? <FaPause /> : <FaPlay />}</div>
          <div className="nav-bot-text">
            {isPlaying ? (
              <>
                {!isLoading && <span>{formatTime(currentTime)}/{formatTime(duration ?? 0)}</span>}
                {isLoading && <div className={theaterMode ? 'spinner' : 'spinner-light'}></div>}
                {duration !== null && !isLoading && !isAI && (
                  <input
                    type="range"
                    min={0}
                    max={duration}
                    value={seekValue}
                    onPointerDown={() => setSeeking(true)}
                    onPointerUp={(e) => {
                      setSeeking(false);
                      handleSeek(seekValue);
                    }}
                    onChange={handleSeekChange}
                    className="audioRange"
                    aria-label="Audio seek slider"
                  />
                )}
              </>
            ) : (
              "Play"
            )}
          </div>
        </div>
      </Link>
      {/* {isPlaying && duration !== null && (
        <input
          type="range"
          min={0}
          max={duration}
          value={seekValue}
          onPointerDown={() => setSeeking(true)}
          onPointerUp={(e) => {
            setSeeking(false);
            handleSeek(seekValue);
          }}
          onChange={handleSeekChange}
          className="audioRange"
          aria-label="Audio seek slider"
        />
      )} */}
    </>
  );
};

export default GlobalAudioPlayer;